import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatGroup, Chat } from '../types';
import { renameChat, deleteChat, fetchUser } from '../api';
import { useModal } from '../context/ModalContext';
import styles from './ChatApp.module.css';

interface SidebarProps {
  chats: ChatGroup[];
  toggleSidebar: () => void;
  setActiveChat: (chat: Chat) => void;
  setChats: React.Dispatch<React.SetStateAction<ChatGroup[]>>;
  activeChatUuid?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ chats, toggleSidebar, setActiveChat, setChats, activeChatUuid }) => {
  const [editingChatUuid, setEditingChatUuid] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState<string>('');
  const { openModal } = useModal();
  const navigate = useNavigate();

  const createNewChat = () => {
    navigate('/chat/'); // Переход на пустой чат, создание произойдет в ChatArea при отправке сообщения
  };

  const selectChat = (uuid: string) => {
    const chat = chats.flatMap((g) => g.chats).find((c) => c.uuid === uuid);
    if (chat && !editingChatUuid) {
      setActiveChat(chat);
      navigate(`/chat/${uuid}`);
    }
  };

  const openMenu = (e: React.MouseEvent, uuid: string) => {
    e.stopPropagation();
    const popup = document.getElementById('chatMenu') as HTMLDivElement;
    if (popup) {
      const rect = (e.currentTarget as HTMLElement).closest(`.${styles.chatItem}`)!.getBoundingClientRect();
      popup.style.display = 'block';
      popup.style.top = `${rect.top + window.scrollY}px`;
      popup.style.left = `${rect.right + window.scrollX + 10}px`;
      popup.dataset.chatUuid = uuid;

      popup.innerHTML = `
        <button class="${styles.chatOption}" onclick="window.Chat.rename(event)">Переименовать</button>
        <button class="${styles.chatOption} ${styles.chatOptionDelete}" onclick="window.Chat.delete(event)">Удалить</button>
      `;
      (window as any).Chat = {
        rename: (event: Event) => {
          event.stopPropagation();
          startRename(uuid);
          popup.style.display = 'none';
        },
        delete: (event: Event) => {
          event.stopPropagation();
          handleDelete(uuid);
          popup.style.display = 'none';
        },
      };
    }
  };

  const startRename = (uuid: string) => {
    setEditingChatUuid(uuid);
    const chat = chats.flatMap((g) => g.chats).find((c) => c.uuid === uuid);
    setNewTitle(chat?.title || '');
  };

  const saveRename = async (uuid: string) => {
    try {
      const updatedChat = await renameChat(uuid, newTitle);
      setChats((prev) =>
        prev.map((group) => ({
          ...group,
          chats: group.chats.map((c) => (c.uuid === uuid ? { ...c, title: updatedChat.title } : c)),
        }))
      );
      if (uuid === activeChatUuid) {
        setActiveChat({ ...updatedChat, title: updatedChat.title });
      }
      setEditingChatUuid(null);
    } catch (error) {
      console.error('Ошибка при переименовании чата:', error);
    }
  };

  const cancelRename = () => {
    setEditingChatUuid(null);
  };

  const handleDelete = async (uuid: string) => {
    try {
      await deleteChat(uuid);
      setChats((prev) =>
        prev.map((group) => ({
          ...group,
          chats: group.chats.filter((c) => c.uuid !== uuid),
        }))
      );
      if (uuid === activeChatUuid) {
        navigate('/chat/');
      }
    } catch (error) {
      console.error('Ошибка при удалении чата:', error);
    }
  };

  const handleOpenSettings = async () => {
    try {
      const userData = await fetchUser();
      if (!userData.is_authenticated) {
        navigate('/login');
        return;
      }
      openModal('modelSettingsModal');
    } catch (error) {
      console.error('Ошибка проверки авторизации:', error);
      navigate('/login');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const popup = document.getElementById('chatMenu') as HTMLDivElement;
      const target = e.target as Element | null;
      if (popup && target && !target.closest(`.${styles.chatMenuBtn}`) && !target.closest('#chatMenu')) {
        popup.style.display = 'none';
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <div className={styles.sidebarTop}>
          <button className={styles.sidebarToggleBtn} onClick={toggleSidebar}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <button className={`${styles.newChatBtn} ${styles.newChatLogo}`} onClick={createNewChat}>
            GPT4Chats<span>.</span>
          </button>
        </div>
        <button className={styles.newChatFullBtn} onClick={createNewChat}>
          <i className="fas fa-plus"></i> Новый чат
        </button>
      </div>

      <div className={styles.chatList}>
        {chats.map((group) =>
          group.chats.length > 0 ? (
            <div className={styles.timeGroup} key={group.group_title}>
              <h4 className={styles.groupTitle}>{group.group_title}</h4>
              {group.chats.map((chat) => (
                <div
                  className={`${styles.chatItem} ${chat.uuid === activeChatUuid ? styles.chatItemActive : ''}`}
                  key={chat.uuid}
                  data-chat-id={chat.uuid}
                  data-is-editing={editingChatUuid === chat.uuid ? 'true' : 'false'}
                  onClick={() => selectChat(chat.uuid)}
                >
                  <div className={styles.chatItemHeader}>
                    {editingChatUuid === chat.uuid ? (
                      <span className={styles.chatTitle}>
                        <input
                          type="text"
                          className={`${styles.modalInput} ${styles.inlineEdit}`}
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(chat.uuid);
                            if (e.key === 'Escape') cancelRename();
                          }}
                        />
                        <div className={styles.modalActions}>
                          <button className={`${styles.modalBtn} ${styles.cancelBtn}`} onClick={cancelRename}>
                            Отмена
                          </button>
                          <button
                            className={`${styles.modalBtn} ${styles.saveBtn}`}
                            onClick={() => saveRename(chat.uuid)}
                          >
                            Сохранить
                          </button>
                        </div>
                      </span>
                    ) : (
                      <>
                        <span className={styles.chatTitle}>{chat.title}</span>
                        <button className={styles.chatMenuBtn} onClick={(e) => openMenu(e, chat.uuid)}>
                          <i className="fas fa-ellipsis-v"></i>
                        </button>
                      </>
                    )}
                  </div>
                  <div className={styles.chatPreview}>
                    <span className={styles.previewFull} style={{ display: 'none' }}>{chat.preview}</span>
                    <span>{chat.preview.slice(0, 50)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : null
        )}
      </div>

      <div className={styles.sidebarFooter}>
        <button className={styles.settingsBtn} onClick={handleOpenSettings}>
          <i className="fas fa-cog"></i>
          <span>Настройки модели</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;